import type { JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { getZone, hasZone } from '../../content';
import { COLLECTIBLE_HINTS } from '../../content/quests';
import type { CharacterSave } from '../../game/character';
import { allQuests, nextMainQuest } from '../../game/quests';
import type { NavGrid } from '../../sim/navGrid';
import { api } from '../../ui/app/api';
import { openPanel, ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { buildNav } from '../../zones/build';
import type { ZoneLayout } from '../../zones/types';
import { Amount, Empty, TipLayer, getGame, useCtx, useNow } from '../economy/ui';
import { allWaystones, travelCost, travelToWaystone } from '../travel';
import { TRAVEL_FROM } from '../travel/waystones';
import { type MapIcon, questFocus, worldZone, zoneMapIcons } from './mapData';
import { zoneRaster } from './mapRender';
import { npcName, npcRole, npcZone, zoneName } from './names';
import { QuestBadge } from './QuestsPanel';

type Layer = 'quests' | 'npcs' | 'travel' | 'collect';
const LAYERS: { id: Layer; label: string }[] = [
  { id: 'quests', label: 'Quests' },
  { id: 'npcs', label: 'People' },
  { id: 'travel', label: 'Travel' },
  { id: 'collect', label: 'Collectibles' },
];
const LAYER_OF: Record<MapIcon['kind'], Layer> = {
  objective: 'quests',
  npcQuest: 'quests',
  npcTurnIn: 'quests',
  npc: 'npcs',
  waystone: 'travel',
  exit: 'travel',
  dock: 'travel',
  dungeon: 'travel',
  seed: 'collect',
};

function safeLayout(id: string): ZoneLayout | null {
  try {
    return hasZone(id) ? getZone(id) : null;
  } catch {
    return null;
  }
}

const navCache = new Map<string, NavGrid>();
/** The live zone's nav grid, or one built (once) for a zone we are not in. */
function navFor(layout: ZoneLayout): NavGrid | null {
  const live = getGame()?.zone;
  if (live?.layout.id === layout.id) return live.nav;
  let nav = navCache.get(layout.id);
  if (!nav) {
    try {
      nav = buildNav(layout, 0.5);
      navCache.set(layout.id, nav);
    } catch {
      return null;
    }
  }
  return nav;
}

// ================================================================ region map

interface View {
  cx: number;
  cz: number;
  span: number;
}

function fitView(l: ZoneLayout): View {
  return {
    cx: l.minX + l.width / 2,
    cz: l.minZ + l.depth / 2,
    span: Math.max(l.width, l.depth) * 1.02,
  };
}

function IconShape({ ic, k, focus }: { ic: MapIcon; k: number; focus: boolean }) {
  const s = 7 * k;
  switch (ic.kind) {
    case 'objective':
      return (
        <g class={`mp-obj k-${ic.questKind ?? 'side'}${focus ? ' is-focus' : ''}`}>
          <circle cx={ic.x} cy={ic.z} r={Math.max(ic.r ?? 4, 9 * k)} class="mp-obj-area" />
          <rect
            x={ic.x - s * 0.75}
            y={ic.z - s * 0.75}
            width={s * 1.5}
            height={s * 1.5}
            transform={`rotate(45 ${ic.x} ${ic.z})`}
            class="mp-obj-dia"
          />
        </g>
      );
    case 'waystone':
      return (
        <g class={`mp-way${ic.on ? ' is-on' : ''}`}>
          <path
            d={`M ${ic.x} ${ic.z - s * 1.2} L ${ic.x + s * 0.75} ${ic.z} L ${ic.x} ${ic.z + s * 1.2} L ${ic.x - s * 0.75} ${ic.z} Z`}
          />
          <circle cx={ic.x} cy={ic.z} r={s * 0.28} class="mp-way-core" />
          {k < 0.2 && (
            <text x={ic.x} y={ic.z - s * 1.6} font-size={10 * k} class="mp-label is-way">
              {ic.label}
            </text>
          )}
        </g>
      );
    case 'exit':
    case 'dock':
      return (
        <g class={`mp-exit${ic.on ? '' : ' is-locked'}`}>
          <circle cx={ic.x} cy={ic.z} r={s * 1.05} />
          <path
            d={`M ${ic.x - s * 0.45} ${ic.z} L ${ic.x + s * 0.45} ${ic.z} M ${ic.x + s * 0.1} ${ic.z - s * 0.35} L ${ic.x + s * 0.45} ${ic.z} L ${ic.x + s * 0.1} ${ic.z + s * 0.35}`}
            class="mp-exit-arrow"
          />
          <text x={ic.x} y={ic.z - s * 1.6} font-size={11 * k} class="mp-label">
            {ic.label}
          </text>
        </g>
      );
    case 'dungeon':
      return (
        <g class={`mp-dun${ic.on ? '' : ' is-locked'}`}>
          <path
            d={`M ${ic.x - s} ${ic.z + s * 0.8} L ${ic.x - s} ${ic.z - s * 0.2} A ${s} ${s} 0 0 1 ${ic.x + s} ${ic.z - s * 0.2} L ${ic.x + s} ${ic.z + s * 0.8} Z`}
          />
          <path
            d={`M ${ic.x - s * 0.45} ${ic.z + s * 0.8} L ${ic.x - s * 0.45} ${ic.z} A ${s * 0.45} ${s * 0.45} 0 0 1 ${ic.x + s * 0.45} ${ic.z} L ${ic.x + s * 0.45} ${ic.z + s * 0.8} Z`}
            class="mp-dun-door"
          />
          <text x={ic.x} y={ic.z - s * 1.5} font-size={11 * k} class="mp-label is-dun">
            {ic.label}
          </text>
        </g>
      );
    case 'npcQuest':
    case 'npcTurnIn':
      return (
        <g class="mp-npcq">
          <rect
            x={ic.x - s * 0.8}
            y={ic.z - s * 0.8}
            width={s * 1.6}
            height={s * 1.6}
            transform={`rotate(45 ${ic.x} ${ic.z})`}
          />
          <text x={ic.x} y={ic.z + s * 0.45} font-size={13 * k} class="mp-npcq-t">
            {ic.kind === 'npcQuest' ? '!' : '?'}
          </text>
          {k < 0.16 && (
            <text x={ic.x} y={ic.z - s * 1.4} font-size={10 * k} class="mp-label is-npc">
              {ic.label}
            </text>
          )}
        </g>
      );
    case 'npc':
      return (
        <g>
          <circle cx={ic.x} cy={ic.z} r={s * 0.45} class="mp-npc" />
          {k < 0.16 && (
            <text x={ic.x} y={ic.z - s * 0.9} font-size={10 * k} class="mp-label is-npc">
              {ic.label}
            </text>
          )}
        </g>
      );
    case 'seed':
      return (
        <g class="mp-seed">
          <circle cx={ic.x} cy={ic.z} r={s * 0.55} />
          <circle cx={ic.x} cy={ic.z} r={s * 0.22} class="mp-seed-core" />
        </g>
      );
  }
}

interface HoverTip {
  ic: MapIcon;
  x: number;
  y: number;
}

function ZoneMap({
  layout,
  icons,
  player,
  focus,
  layers,
}: {
  layout: ZoneLayout;
  icons: MapIcon[];
  player: { x: number; z: number; facing: number } | null;
  focus: { x: number; z: number } | null;
  layers: Set<Layer>;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [px, setPx] = useState(600);
  const [view, setView] = useState<View>(() =>
    focus ? { cx: focus.x, cz: focus.z, span: 130 } : fitView(layout),
  );
  const [tip, setTip] = useState<HoverTip | null>(null);
  const drag = useRef<{ x: number; y: number; cx: number; cz: number; moved: boolean } | null>(
    null,
  );
  const raster = zoneRaster(layout, navFor(layout));

  useEffect(
    () => setView(focus ? { cx: focus.x, cz: focus.z, span: 130 } : fitView(layout)),
    [layout.id, focus?.x, focus?.z],
  );
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setPx(Math.max(100, Math.min(el.clientWidth, el.clientHeight)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const k = view.span / px;
  const maxSpan = Math.max(layout.width, layout.depth) * 1.3;
  const zoomBy = (f: number, at?: { x: number; z: number }) =>
    setView((v) => {
      const span = Math.max(24, Math.min(maxSpan, v.span * f));
      const a = at ?? { x: v.cx, z: v.cz };
      return {
        cx: a.x + (v.cx - a.x) * (span / v.span),
        cz: a.z + (v.cz - a.z) * (span / v.span),
        span,
      };
    });
  const worldAt = (e: { clientX: number; clientY: number }): { x: number; z: number } => {
    const el = box.current;
    if (!el) return { x: view.cx, z: view.cz };
    const r = el.getBoundingClientRect();
    return {
      x: view.cx + (e.clientX - r.left - r.width / 2) * k,
      z: view.cz + (e.clientY - r.top - r.height / 2) * k,
    };
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    zoomBy(e.deltaY > 0 ? 1.18 : 1 / 1.18, worldAt(e));
  };
  const onDown = (e: PointerEvent) => {
    // Buttons over the map keep their clicks (capturing would retarget them).
    if (e.button !== 0 || (e.target as Element | null)?.closest('button')) return;
    drag.current = { x: e.clientX, y: e.clientY, cx: view.cx, cz: view.cz, moved: false };
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    setView((v) => ({ ...v, cx: d.cx - dx * k, cz: d.cz - dy * k }));
  };
  const onUp = () => (drag.current = null);
  // The window is transformed, so the tooltip is placed relative to the map box.
  const tipAt = (ic: MapIcon, e: { clientX: number; clientY: number }): HoverTip => {
    const r = box.current?.getBoundingClientRect();
    return { ic, x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  const shown = icons.filter((ic) => layers.has(LAYER_OF[ic.kind]));
  const order: MapIcon['kind'][] = [
    'objective',
    'npc',
    'seed',
    'exit',
    'dock',
    'dungeon',
    'waystone',
    'npcTurnIn',
    'npcQuest',
  ];
  shown.sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  const vb = `${view.cx - view.span / 2} ${view.cz - view.span / 2} ${view.span} ${view.span}`;
  return (
    <div
      class="mp-map"
      ref={box}
      onWheel={onWheel}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={() => setTip(null)}
    >
      <svg viewBox={vb} preserveAspectRatio="xMidYMid meet" class="mp-svg">
        <rect
          x={layout.minX - 400}
          y={layout.minZ - 400}
          width={layout.width + 800}
          height={layout.depth + 800}
          class="mp-void"
        />
        {raster ? (
          <image
            href={raster.url}
            x={layout.minX}
            y={layout.minZ}
            width={layout.width}
            height={layout.depth}
            preserveAspectRatio="none"
          />
        ) : (
          <rect
            x={layout.minX}
            y={layout.minZ}
            width={layout.width}
            height={layout.depth}
            class="mp-plain"
          />
        )}
        <rect
          x={layout.minX}
          y={layout.minZ}
          width={layout.width}
          height={layout.depth}
          class="mp-frame"
          stroke-width={2 * k}
        />
        {shown.map((ic, i) => (
          <g
            key={`${ic.kind}:${ic.id ?? ic.questId ?? ''}:${i}`}
            class="mp-icon"
            onPointerEnter={(e) => setTip(tipAt(ic, e))}
            onPointerMove={(e) => setTip(tipAt(ic, e))}
            onPointerLeave={() => setTip(null)}
          >
            <IconShape
              ic={ic}
              k={k}
              focus={!!focus && Math.hypot(ic.x - focus.x, ic.z - focus.z) < 1}
            />
          </g>
        ))}
        {player && (
          <g
            class="mp-player"
            transform={`translate(${player.x} ${player.z}) rotate(${180 - (player.facing * 180) / Math.PI})`}
          >
            <circle r={9 * k} class="mp-player-ring" stroke-width={1.6 * k} />
            <path
              d={`M 0 ${-8 * k} L ${5.5 * k} ${6 * k} L 0 ${3 * k} L ${-5.5 * k} ${6 * k} Z`}
              class="mp-player-arrow"
            />
          </g>
        )}
      </svg>
      <div class="mp-zoom">
        <button type="button" title="Zoom in" onClick={() => zoomBy(1 / 1.35)}>
          +
        </button>
        <button type="button" title="Zoom out" onClick={() => zoomBy(1.35)}>
          −
        </button>
        {player && (
          <button
            type="button"
            title="Centre on me"
            onClick={() =>
              setView((v) => ({ ...v, cx: player.x, cz: player.z, span: Math.min(v.span, 140) }))
            }
          >
            ◎
          </button>
        )}
        <button type="button" title="Whole region" onClick={() => setView(fitView(layout))}>
          ⤢
        </button>
      </div>
      <div class="mp-compass" aria-hidden="true">
        N
      </div>
      {tip && (
        <div
          class="mp-tip"
          style={{
            left: `${tip.x + 300 > (box.current?.clientWidth ?? 0) ? tip.x - 294 : tip.x + 14}px`,
            top: `${tip.y + 12}px`,
          }}
        >
          <div class="mp-tip-t">{tip.ic.label}</div>
          {tip.ic.sub && <div class="mp-tip-s">{tip.ic.sub}</div>}
        </div>
      )}
    </div>
  );
}

function TravelRow({
  w,
  c,
  zoneId,
}: {
  w: ReturnType<typeof allWaystones>[number];
  c: CharacterSave;
  zoneId: string | null;
}) {
  const game = getGame();
  const attuned = c.waystones.includes(w.id);
  const pos = game?.player?.pos;
  const here = !!pos && w.zone === zoneId && Math.hypot(pos.x - w.x, pos.z - w.z) < 14;
  const canLeave = TRAVEL_FROM.has(game?.zone?.layout.kind ?? '');
  const cost = travelCost(c.level, w.zone === zoneId);
  const silver = game?.account.wallet.silver ?? 0;
  return (
    <div class={`mp-way-row${attuned ? '' : ' is-locked'}`}>
      <i class={`mp-way-ic${attuned ? ' is-on' : ''}`} aria-hidden="true" />
      <div class="mp-way-n">
        <div>{w.name}</div>
        <div class="fx-sub">
          {attuned
            ? here
              ? 'You are here'
              : w.zone === zoneId
                ? 'This region · half fare'
                : w.zoneName
            : 'Not attuned'}
        </div>
      </div>
      {attuned && !here && <Amount kind="silver" n={cost} bad={silver < cost} />}
      <button
        class="btn btn--gold sm"
        disabled={!attuned || here || !canLeave || silver < cost}
        onClick={() => travelToWaystone(w)}
      >
        Travel
      </button>
    </div>
  );
}

// ================================================================ world map

const WORLD_NODES: { id: string; x: number; y: number; r: number; fill: string }[] = [
  { id: 'haven', x: 150, y: 142, r: 62, fill: '#5d8a4c' },
  { id: 'glimmer', x: 432, y: 92, r: 54, fill: '#3f9480' },
  { id: 'havenreach', x: 420, y: 318, r: 76, fill: '#8c806a' },
  { id: 'emberwild', x: 348, y: 500, r: 104, fill: '#62803e' },
  { id: 'sunscar', x: 655, y: 425, r: 120, fill: '#b58c52' },
  { id: 'frosthollow', x: 872, y: 250, r: 108, fill: '#90a4bb' },
];
const LAND_ROUTES: [string, string][] = [
  ['emberwild', 'havenreach'],
  ['emberwild', 'sunscar'],
  ['havenreach', 'sunscar'],
  ['sunscar', 'frosthollow'],
];
const SEA_ROUTES: [string, string][] = [
  ['havenreach', 'haven'],
  ['havenreach', 'glimmer'],
];

/** Deterministic blobby island outline. */
function blob(cx: number, cy: number, r: number, seed: number): string {
  const n = 14;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const wob =
      0.82 + 0.18 * Math.sin(seed * 12.9898 + i * 78.233) * Math.sin(seed * 3.1 + i * 1.7);
    pts.push([cx + Math.cos(a) * r * wob * 1.12, cy + Math.sin(a) * r * wob * 0.9]);
  }
  const mid = (a: [number, number], b: [number, number]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  let d = '';
  for (let i = 0; i < n; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % n]!;
    const m = mid(p, q);
    d += i === 0 ? `M ${mid(pts[n - 1]!, p).join(' ')} ` : '';
    d += `Q ${p[0].toFixed(1)} ${p[1].toFixed(1)} ${m[0]!.toFixed(1)} ${m[1]!.toFixed(1)} `;
  }
  return `${d}Z`;
}

/** A point of a zone layout placed inside its island on the world map. */
function inNode(
  node: (typeof WORLD_NODES)[number],
  l: ZoneLayout,
  x: number,
  z: number,
): { x: number; y: number } {
  const u = (x - (l.minX + l.width / 2)) / (l.width / 2);
  const v = (z - (l.minZ + l.depth / 2)) / (l.depth / 2);
  return { x: node.x + u * node.r * 0.8, y: node.y + v * node.r * 0.68 };
}

function WorldMap({
  c,
  here,
  sel,
  onSelect,
  mainAt,
}: {
  c: CharacterSave;
  here: string | null;
  sel: string | null;
  onSelect: (id: string) => void;
  mainAt: { zone: string; x?: number; z?: number } | null;
}) {
  const node = (id: string) => WORLD_NODES.find((n) => n.id === id);
  const player = api().player;
  return (
    <svg class="mp-world" viewBox="0 0 1000 620" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="mp-sea" gradientUnits="userSpaceOnUse" cx="470" cy="300" r="760">
          <stop offset="0" stop-color="#1b4550" />
          <stop offset="1" stop-color="#0a1c22" />
        </radialGradient>
        <pattern id="mp-waves" width="46" height="22" patternUnits="userSpaceOnUse">
          <path d="M 2 12 q 6 -6 12 0 t 12 0" fill="none" stroke="#ffffff10" stroke-width="1" />
        </pattern>
      </defs>
      <rect x="-400" y="-300" width="1800" height="1220" fill="url(#mp-sea)" />
      <rect x="-400" y="-300" width="1800" height="1220" fill="url(#mp-waves)" />
      <text x="170" y="360" class="mp-sea-label">
        The Gleaming Sea
      </text>
      <g class="mp-rose" transform="translate(930 560)">
        <circle r="30" />
        <path d="M 0 -34 L 6 0 L 0 34 L -6 0 Z M -34 0 L 0 -5 L 34 0 L 0 5 Z" />
        <text y="-40">N</text>
      </g>
      {SEA_ROUTES.map(([a, b]) => {
        const p = node(a);
        const q = node(b);
        return p && q ? (
          <path
            key={`${a}-${b}`}
            d={`M ${p.x} ${p.y} Q ${(p.x + q.x) / 2 - 60} ${(p.y + q.y) / 2} ${q.x} ${q.y}`}
            class="mp-route sea"
          />
        ) : null;
      })}
      {WORLD_NODES.map((n, i) => (
        <path key={`surf-${n.id}`} d={blob(n.x, n.y, n.r + 16, i + 1)} class="mp-surf" />
      ))}
      {WORLD_NODES.map((n, i) => (
        <path key={`land-${n.id}`} d={blob(n.x, n.y, n.r + 7, i + 1)} class="mp-land" />
      ))}
      {WORLD_NODES.map((n, i) => {
        const l = safeLayout(n.id);
        if (!l) return null;
        const isSel = sel === n.id;
        return (
          <g
            key={n.id}
            class={`mp-node${isSel ? ' is-sel' : ''}${here === n.id ? ' is-here' : ''}`}
            onClick={() => onSelect(n.id)}
          >
            <path d={blob(n.x, n.y, n.r, i + 1)} fill={n.fill} class="mp-region" />
            {l.interactables
              .filter((it) => it.kind === 'waystone')
              .map((w) => {
                const p = inNode(n, l, w.x, w.z);
                const on = c.waystones.includes(w.id);
                return (
                  <path
                    key={w.id}
                    d={`M ${p.x} ${p.y - 6} L ${p.x + 4} ${p.y} L ${p.x} ${p.y + 6} L ${p.x - 4} ${p.y} Z`}
                    class={`mp-wmark${on ? ' is-on' : ''}`}
                  />
                );
              })}
            {l.portals
              .filter((p) => p.kind === 'dungeon')
              .map((p) => {
                const q = inNode(n, l, p.x, p.z);
                return (
                  <path
                    key={p.id}
                    d={`M ${q.x - 6} ${q.y + 5} L ${q.x - 6} ${q.y - 1} A 6 6 0 0 1 ${q.x + 6} ${q.y - 1} L ${q.x + 6} ${q.y + 5} Z`}
                    class={`mp-dmark${c.flags[`${p.to}_cleared`] ? ' is-cleared' : ''}`}
                  />
                );
              })}
            <text x={n.x} y={n.y - 6} class="mp-node-n">
              {l.name}
            </text>
            <text x={n.x} y={n.y + 13} class="mp-node-l">
              {l.kind === 'town' ? 'City' : `Lv ${l.levelRange[0]}–${l.levelRange[1]}`}
            </text>
            {here === n.id &&
              player &&
              (() => {
                const p = inNode(n, l, player.pos.x, player.pos.z);
                return <circle cx={p.x} cy={p.y} r="6" class="mp-you" />;
              })()}
            {mainAt?.zone === n.id &&
              (() => {
                const p =
                  mainAt.x !== undefined && mainAt.z !== undefined
                    ? inNode(n, l, mainAt.x, mainAt.z)
                    : { x: n.x + n.r * 0.55, y: n.y - n.r * 0.45 };
                return (
                  <rect
                    x={p.x - 6}
                    y={p.y - 6}
                    width="12"
                    height="12"
                    transform={`rotate(45 ${p.x} ${p.y})`}
                    class="mp-mainmark"
                  />
                );
              })()}
          </g>
        );
      })}
      {LAND_ROUTES.map(([a, b]) => {
        const p = node(a);
        const q = node(b);
        return p && q ? (
          <path key={`${a}-${b}`} d={`M ${p.x} ${p.y} L ${q.x} ${q.y}`} class="mp-route" />
        ) : null;
      })}
    </svg>
  );
}

function WorldPane({
  id,
  c,
  zoneId,
  onView,
}: {
  id: string;
  c: CharacterSave;
  zoneId: string | null;
  onView: () => void;
}) {
  const l = safeLayout(id);
  const game = getGame();
  if (!l || !game) return null;
  const wz = worldZone(l, c, game.account, safeLayout);
  const ways = allWaystones().filter((w) => w.zone === id);
  return (
    <div class="mp-pane-body">
      <h3 class="mp-pane-t">{wz.name}</h3>
      <div class="fx-sub">
        {wz.subtitle ? `${wz.subtitle} · ` : ''}
        {wz.kind === 'town' ? 'City' : `Levels ${wz.levelRange[0]}–${wz.levelRange[1]}`}
      </div>
      <button class="btn sm mp-view" onClick={onView}>
        Open region map
      </button>
      {wz.quests.length > 0 && (
        <>
          <div class="section-h">Quests here</div>
          {wz.quests.map((q) => (
            <button
              key={q.id}
              class={`mp-q k-${q.kind}`}
              onClick={() => openPanel('quests', `quest:${q.id}`)}
            >
              <QuestBadge kind={q.kind} />
              <span>{q.name}</span>
              {q.ready && <span class="mp-q-ready">Complete</span>}
            </button>
          ))}
        </>
      )}
      {wz.available > 0 && (
        <div class="mp-avail">
          {wz.available} side quest{wz.available > 1 ? 's' : ''} available here
        </div>
      )}
      {ways.length > 0 && (
        <>
          <div class="section-h">Waystones</div>
          {ways.map((w) => (
            <TravelRow key={w.id} w={w} c={c} zoneId={zoneId} />
          ))}
        </>
      )}
      {wz.dungeons.length > 0 && (
        <>
          <div class="section-h">Dungeons</div>
          {wz.dungeons.map((d) => (
            <div key={d.id} class="mp-dun-row">
              <i class={`mp-dun-ic${d.cleared ? ' is-cleared' : ''}`} aria-hidden="true" />
              <span>{d.name}</span>
              <span class="fx-dim fx-num">Lv {d.minLevel}+</span>
              {d.cleared && <span class="mp-cleared">Cleared</span>}
            </div>
          ))}
        </>
      )}
      {id === 'havenreach' && c.flags.story_complete && (
        <p class="mp-note">
          Rift Delve, Colossus Hunts, the Deepvault and the Warlord Siege open from Elowen in Gate
          Square.
        </p>
      )}
      {wz.seeds.total > 0 && (
        <div class="mp-seeds">
          <i class="mp-seed-ic" aria-hidden="true" /> Glimmerseeds{' '}
          <b class="fx-num">
            {wz.seeds.have}/{wz.seeds.total}
          </b>
        </div>
      )}
    </div>
  );
}

// ================================================================ panel

/** Map (M): the current region with quest markers, and the world with waystone travel. */
export function MapPanel() {
  const ctx = useCtx();
  useNow(250);
  const arg = ui.panelArg.value;
  const gameApi = api();
  const liveZone = gameApi.zoneId;
  const [tab, setTab] = useState<'region' | 'world'>('region');
  const [viewZone, setViewZone] = useState<string | null>(null);
  const [worldSel, setWorldSel] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ x: number; z: number } | null>(null);
  const [layers, setLayers] = useState<Set<Layer>>(() => new Set(LAYERS.map((l) => l.id)));

  useEffect(() => {
    if (!arg?.startsWith('quest:') || !ctx) return;
    const f = questFocus(ctx.c, arg.slice('quest:'.length), npcZone);
    if (!f) return;
    if (f.zone === liveZone || (f.x !== undefined && safeLayout(f.zone)?.kind !== 'dungeon')) {
      setViewZone(f.zone === liveZone ? null : f.zone);
      setFocus(f.x !== undefined && f.z !== undefined ? { x: f.x, z: f.z } : null);
      setTab('region');
    } else {
      setWorldSel(f.zone);
      setTab('world');
    }
  }, [arg]);
  if (!ctx) return null;
  const { c, a } = ctx;
  const regionId = viewZone ?? liveZone ?? c.zone;
  const layout = safeLayout(regionId);
  const game = getGame();
  const live = game?.zone?.layout.id === regionId ? game.zone : null;
  const present = live ? new Set(live.interactables.map((i) => i.id)) : undefined;
  const icons = layout
    ? zoneMapIcons(layout, c, a, {
        npcName,
        npcRole,
        zoneName,
        npcZone,
        hints: COLLECTIBLE_HINTS,
        present,
      })
    : [];
  const p = live ? gameApi.player : null;
  const player = p ? { x: p.pos.x, z: p.pos.z, facing: p.facing } : null;
  const objectives = icons.filter((i) => i.kind === 'objective');
  const hereWorld =
    game?.zone && TRAVEL_FROM.has(game.zone.layout.kind)
      ? game.zone.layout.id
      : (game?.returnPoint?.zone ?? c.zone);
  const activeMain = allQuests().find((q) => q.kind === 'main' && c.quests.active[q.id]);
  const nm = activeMain ?? nextMainQuest(c);
  const mainAt = nm ? questFocus(c, nm.id, npcZone) : null;
  const toggle = (id: Layer) =>
    setLayers((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const ways = allWaystones().filter((w) => w.zone === regionId);
  const exits = icons.filter((i) => i.kind === 'exit' || i.kind === 'dock' || i.kind === 'dungeon');
  const worldId = worldSel ?? hereWorld;
  const tabBtn = (id: 'region' | 'world', label: string): JSX.Element => (
    <button class="tab" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}>
      {label}
    </button>
  );
  return (
    <Window
      title="Map"
      width="min(1180px, 96vw)"
      height="min(800px, 92vh)"
      className="fx-win mp-win"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          {tabBtn('region', layout ? layout.name : 'Region')}
          {tabBtn('world', 'Sundered Isles')}
        </div>
        <span class="fx-spacer" />
        {tab === 'region' && viewZone && viewZone !== liveZone && (
          <button class="btn sm" onClick={() => (setViewZone(null), setFocus(null))}>
            Back to {liveZone ? zoneName(liveZone) : 'my region'}
          </button>
        )}
        <span class="mp-count">
          <span class="fx-h">Waystones</span>{' '}
          <b class="fx-num">
            {allWaystones().filter((w) => c.waystones.includes(w.id)).length}/
            {allWaystones().length}
          </b>
        </span>
      </div>
      <div class="fx-rule" />
      {tab === 'region' ? (
        <div class="mp-body">
          {layout ? (
            <ZoneMap layout={layout} icons={icons} player={player} focus={focus} layers={layers} />
          ) : (
            <Empty title="No map for this place" />
          )}
          <aside class="mp-side fx-scroll">
            {layout && (
              <>
                <h3 class="mp-pane-t">{layout.name}</h3>
                <div class="fx-sub">
                  {layout.subtitle ? `${layout.subtitle} · ` : ''}
                  {layout.kind === 'town'
                    ? 'City'
                    : `Levels ${layout.levelRange[0]}–${layout.levelRange[1]}`}
                </div>
              </>
            )}
            <div class="mp-layers">
              {LAYERS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  class={`mp-layer l-${l.id}${layers.has(l.id) ? ' is-on' : ''}`}
                  aria-pressed={layers.has(l.id)}
                  onClick={() => toggle(l.id)}
                >
                  <i aria-hidden="true" />
                  {l.label}
                </button>
              ))}
            </div>
            <div class="section-h">Objectives</div>
            {objectives.length === 0 && (
              <div class="fx-dim mp-none">No active objectives in this region.</div>
            )}
            {objectives.map((o, i) => (
              <button
                key={i}
                class={`mp-q k-${o.questKind ?? 'side'}`}
                onClick={() => setFocus({ x: o.x, z: o.z })}
              >
                <QuestBadge kind={o.questKind ?? 'side'} />
                <span>
                  {o.label}
                  <small>{o.sub}</small>
                </span>
              </button>
            ))}
            {ways.length > 0 && <div class="section-h">Waystones</div>}
            {ways.map((w) => (
              <TravelRow key={w.id} w={w} c={c} zoneId={liveZone} />
            ))}
            {exits.length > 0 && <div class="section-h">Roads & dungeons</div>}
            {exits.map((e) => (
              <button
                key={e.id}
                class={`mp-exit-row${e.on ? '' : ' is-locked'}`}
                onClick={() => setFocus({ x: e.x, z: e.z })}
              >
                <i class={`mp-ex-ic k-${e.kind}`} aria-hidden="true" />
                <span>{e.label}</span>
                {e.sub && <span class="fx-dim">{e.sub}</span>}
              </button>
            ))}
          </aside>
        </div>
      ) : (
        <div class="mp-body">
          <div class="mp-worldwrap">
            <WorldMap c={c} here={hereWorld} sel={worldId} onSelect={setWorldSel} mainAt={mainAt} />
            <div class="mp-legend">
              <span>
                <i class="lg-you" /> You
              </span>
              <span>
                <i class="lg-main" /> Main story
              </span>
              <span>
                <i class="lg-way" /> Attuned waystone
              </span>
              <span>
                <i class="lg-dun" /> Dungeon
              </span>
            </div>
          </div>
          <aside class="mp-side fx-scroll">
            <WorldPane
              id={worldId}
              c={c}
              zoneId={liveZone}
              onView={() => {
                setViewZone(worldId === liveZone ? null : worldId);
                setFocus(null);
                setTab('region');
              }}
            />
          </aside>
        </div>
      )}
      <TipLayer />
    </Window>
  );
}
